"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArgsPane = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const use_t_1 = require("use-t");
const nano_theme_1 = require("nano-theme");
const ContextPane_1 = require("../ContextPane");
const ContextHeader_1 = require("../ContextHeader");
const ContextSep_1 = require("../ContextSep");
const ContextPaneHeaderSep_1 = require("../ContextPaneHeaderSep");
const ContextPaneFooterSep_1 = require("../ContextPaneFooterSep");
const Scrollbox_1 = require("../../Scrollbox");
const popup_1 = require("../../../utils/popup");
const BasicButtonBack_1 = require("../../../2-inline-block/BasicButton/BasicButtonBack");
const Flex_1 = require("../../../3-list-item/Flex");
const Arg_1 = require("./Arg");
const state_1 = require("./state");
const ContextMenuHeading_1 = require("../ContextMenu/ContextMenuHeading");
const Button_1 = require("../../../2-inline-block/Button");
const BasicTooltip_1 = require("../../BasicTooltip");
const WithShortcut_1 = require("../../../2-inline-block/WithShortcut");
const footerClass = (0, nano_theme_1.rule)({
    d: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    pad: '4px 16px',
    gap: '8px',
});
const nameClass = (0, nano_theme_1.rule)({
    fz: '13px',
    pad: '0 0 0 4px',
});
// Subscribe to all reactive `visible` stores on the current params list and
// trigger a re-render of the pane whenever any value flips.
const useVisibilityVersion = (params) => {
    const [v, setV] = React.useState(0);
    React.useEffect(() => {
        const bump = () => setV((x) => x + 1);
        const unsubs = [];
        for (const p of params) {
            if (p && p.visible) {
                try {
                    unsubs.push(p.visible.subscribe(bump));
                }
                catch { }
            }
        }
        return () => {
            for (const u of unsubs)
                u();
        };
    }, [params]);
    return v;
};
// Approx fixed-chrome heights (header row, separators, apply footer).
const HEADER_H = 40;
const SEPARATOR_H = 7;
const FOOTER_H = 56;
const ArgsPane = (props) => {
    const { item, params, onCancel, minWidth } = props;
    const [t] = (0, use_t_1.useT)();
    const anchor = (0, popup_1.useAnchorPoint)();
    const state = React.useMemo(() => new state_1.ArgsState(props), [props]);
    const args = state.args.use();
    useVisibilityVersion(params);
    const [collapsed, setCollapsed] = React.useState(() => {
        const init = {};
        for (let i = 0; i < params.length; i++) {
            const p = params[i];
            if (p.heading && p.collapsible && p.initialCollapsed) {
                const key = p.id ?? p.name ?? `h${i}`;
                init[key] = true;
            }
        }
        return init;
    });
    const toggleCollapsed = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));
    const handleKeyDown = React.useCallback((e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
        }
    }, [onCancel]);
    const display = item.display?.() ?? t(item.name);
    const rows = [];
    let inCollapsed = false;
    let lastRendered;
    for (let i = 0; i < params.length; i++) {
        const arg = params[i];
        const isHeading = !!arg.heading;
        const isSep = !!arg.sep;
        const isInnerSep = !!arg.innerSep;
        const key = (arg.id ?? arg.name ?? `i${i}`) + (isHeading ? '-h' : isSep ? '-s' : isInnerSep ? '-is' : '');
        if (arg.visible && arg.visible.getSnapshot() === false)
            continue;
        // Inner separator: a padded, inert horizontal line. Doesn't break the
        // current group's collapse state, and doesn't register as a boundary —
        // it's purely decorative and lives inside a group.
        if (isInnerSep) {
            if (inCollapsed)
                continue;
            rows.push(React.createElement("div", { key: key, role: "presentation", "aria-hidden": "true", style: { padding: '3px 16px' } },
                React.createElement("div", { style: {
                        height: 1,
                        background: 'currentColor',
                        opacity: 0.07,
                    } })));
            continue;
        }
        if (isHeading) {
            const groupKey = arg.id ?? arg.name ?? `h${i}`;
            const isCollapsed = !!arg.collapsible && !!collapsed[groupKey];
            const showLine = !!lastRendered;
            const showSpacer = showLine && !lastRendered?.heading && !lastRendered?.sep;
            inCollapsed = isCollapsed;
            rows.push(React.createElement(React.Fragment, { key: key },
                showLine &&
                    (item.compact ? (React.createElement(React.Fragment, null,
                        showSpacer && React.createElement("div", { style: { height: 2 }, "aria-hidden": true }),
                        React.createElement(ContextSep_1.ContextSep, { line: true }))) : (React.createElement(React.Fragment, null,
                        React.createElement(ContextSep_1.ContextSep, null),
                        React.createElement(ContextSep_1.ContextSep, { line: true }),
                        React.createElement(ContextSep_1.ContextSep, null)))),
                React.createElement(ContextMenuHeading_1.ContextMenuHeading, { item: arg, compact: item.compact, collapsed: isCollapsed, onToggle: arg.collapsible ? () => toggleCollapsed(groupKey) : undefined })));
            lastRendered = arg;
            continue;
        }
        if (isSep) {
            inCollapsed = false;
            rows.push(React.createElement(React.Fragment, { key: key },
                React.createElement(ContextSep_1.ContextSep, null),
                React.createElement(ContextSep_1.ContextSep, { line: true }),
                React.createElement(ContextSep_1.ContextSep, null)));
            lastRendered = arg;
            continue;
        }
        if (inCollapsed)
            continue;
        if ((0, state_1.isParam)(arg)) {
            rows.push(React.createElement(React.Fragment, { key: key },
                React.createElement(Arg_1.Arg, { param: arg, value: args[arg.id ?? arg.name], compact: item.compact, onChange: (v) => state.setValue(arg.id ?? arg.name, v), onSubmit: state.onSubmit, focus: i === 0 })));
            lastRendered = arg;
        }
    }
    const submitFooter = props.onSubmit ? (React.createElement(React.Fragment, null,
        lastRendered?.heading ? React.createElement("div", { style: { height: 2 }, "aria-hidden": true }) : React.createElement(ContextSep_1.ContextSep, null),
        React.createElement(ContextSep_1.ContextSep, { line: true }),
        React.createElement(ContextSep_1.ContextSep, null),
        React.createElement("div", { className: footerClass, style: item.compact ? { justifyContent: 'center' } : undefined }, item.compact ? (React.createElement(WithShortcut_1.WithShortcut, { shortcut: "Enter" },
            React.createElement(Button_1.Button, { size: -2, disabled: !state.canSubmit(), onClick: state.onSubmit }, t('Apply')))) : (React.createElement(BasicTooltip_1.BasicTooltip, { shortcut: "\u23CE", renderTooltip: () => 'Enter' },
            React.createElement(Button_1.Button, { disabled: !state.canSubmit(), onClick: state.onSubmit }, t('Apply'))))),
        React.createElement(ContextSep_1.ContextSep, null))) : null;
    if (props.inline) {
        return (React.createElement(React.Fragment, null,
            rows,
            submitFooter));
    }
    // Cap pane width so long values (e.g. a `kind: 'code'` row showing a long
    // path or LaTeX source) can't push the pane to many thousands of pixels.
    // Compact panes get a sensible default; non-compact panes only cap when
    // `item.maxWidth` is set explicitly. Honors `item.maxWidth` either way.
    const paneMaxWidth = item.maxWidth ?? (item.compact ? 480 : undefined);
    const footerHeight = submitFooter ? FOOTER_H : 0;
    const maxScrollHeight = (anchor?.maxHeight() ?? (typeof window !== 'undefined' ? window.innerHeight : 600)) -
        HEADER_H -
        SEPARATOR_H -
        SEPARATOR_H -
        footerHeight;
    return (React.createElement(ContextPane_1.ContextPane, { style: {
            minWidth: minWidth ?? (item.compact ? 333 : 220),
            ...(paneMaxWidth !== undefined ? { maxWidth: paneMaxWidth } : {}),
        }, onKeyDown: handleKeyDown },
        React.createElement(ContextHeader_1.ContextHeader, { compact: true },
            React.createElement(Flex_1.Flex, { style: { alignItems: 'center' } },
                React.createElement(BasicButtonBack_1.BasicButtonBack, { onClick: onCancel }),
                React.createElement("span", { className: nameClass }, display))),
        React.createElement(ContextPaneHeaderSep_1.ContextPaneHeaderSep, null),
        React.createElement(ContextSep_1.ContextSep, null),
        React.createElement(Scrollbox_1.Scrollbox, { style: { maxHeight: maxScrollHeight } }, rows),
        !submitFooter && (React.createElement(React.Fragment, null,
            lastRendered?.heading ? React.createElement("div", { style: { height: 2 }, "aria-hidden": true }) : React.createElement(ContextSep_1.ContextSep, null),
            React.createElement(ContextPaneFooterSep_1.ContextPaneFooterSep, null))),
        submitFooter));
};
exports.ArgsPane = ArgsPane;
